import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import emailChangeService from '../../utils/emailChangeService';
import { useAuth } from '../../hooks/useAuth';

// Components
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

/**
 * Email change verification page
 * This page is used to verify email changes
 */
const VerifyEmailChange = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUserProfile, userProfile } = useAuth();
  
  // Helper function to get the appropriate profile settings URL based on user role
  const getProfileSettingsUrl = () => {
    if (userProfile?.role === 'employer') {
      return '/employer-profile-settings';
    } else if (userProfile?.role === 'va') {
      return '/va-profile-settings';
    }
    // Default fallback - could be customized based on requirements
    return '/va-profile-settings';
  };
  
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [oldEmail, setOldEmail] = useState('');

  useEffect(() => {
    const verifyEmailChange = async () => {
      try {
        const token = searchParams.get('token');
        const email = searchParams.get('email');
        const userId = searchParams.get('userId');
        
        console.log('Email change verification parameters:', { token, email, userId });
        
        if (!token || !email || !userId) {
          console.error('Missing verification parameters');
          setVerificationStatus('error');
          setErrorMessage('Invalid verification link. Missing required parameters.');
          return;
        }

        setUserEmail(email);

        // Use the emailChangeService to confirm the email change
        console.log('Calling emailChangeService.confirmEmailChange');
        const result = await emailChangeService.confirmEmailChange(token, email, userId);
        console.log('Email change confirmation result:', result);
        
        if (!result.success) {
          console.error('Email change confirmation error:', result.error);
          setVerificationStatus('error');
          setErrorMessage(result.error || 'Failed to confirm email change. Please try again.');
          return;
        }

        // Set old email if available in the result
        if (result.data && result.data.oldEmail) {
          setOldEmail(result.data.oldEmail);
        }

        // Refresh user profile to get the updated email
        try {
          await refreshUserProfile();
        } catch (refreshError) {
          console.error('Error refreshing user profile:', refreshError);
          // Continue with success even if refresh fails
        }
        
        setVerificationStatus('success');

        // Redirect to profile settings page after 3 seconds
        setTimeout(() => {
          navigate(getProfileSettingsUrl(), { 
            state: { 
              message: 'Email address successfully updated!',
              emailChanged: true,
              activeTab: 'personal' // Navigate to personal info tab where email is managed
            }
          });
        }, 3000);
      } catch (error) {
        console.error('Verification process error:', error);
        setVerificationStatus('error');
        setErrorMessage('An unexpected error occurred during verification.');
      }
    };

    verifyEmailChange();
  }, [searchParams, navigate, refreshUserProfile]);

  const renderContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <div className="text-center p-4">
            <div className="flex justify-center mb-4">
              <Icon name="Clock" size={60} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Verifying Your New Email</h2>
            <LoadingSpinner />
            <p className="text-gray-600 mt-4">
              Please wait while we update your email address...
            </p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center p-4">
            <div className="flex justify-center mb-4">
              <Icon name="CheckCircle" size={60} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Email Updated Successfully!</h2>
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              Your email has been successfully changed to <strong>{userEmail}</strong>.
              {oldEmail && <p className="mt-1">Previous email: {oldEmail}</p>}
            </div>
            <p className="text-gray-600 mb-4">
              You will be redirected to your profile settings in a few seconds...
            </p>
            <Link to={getProfileSettingsUrl()}>
              <Button variant="primary">
                Go to Profile Settings
              </Button>
            </Link>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-center p-4">
            <div className="flex justify-center mb-4">
              <Icon name="AlertCircle" size={60} className="text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Verification Failed</h2>
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {errorMessage || 'There was a problem updating your email address.'}
            </div>
            <p className="mb-3">
              Please try the following:
            </p>
            <div className="text-left mb-4">
              <ul className="list-disc pl-6">
                <li>Check if you clicked the correct link from your email</li>
                <li>Try requesting an email change again</li>
                <li>Make sure the verification link hasn't expired (valid for 24 hours)</li>
                <li>Contact support if the problem persists</li>
              </ul>
            </div>
            <Link to={getProfileSettingsUrl()}>
              <Button variant="primary">
                Back to Profile Settings
              </Button>
            </Link>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto max-w-md my-8 px-4">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        {renderContent()}
      </div>
    </div>
  );
};

export default VerifyEmailChange;
